import React, { useState, useEffect, useRef, useMemo } from "react";
import { Input, Alert } from "reactstrap";
import SimpleBar from "simplebar-react";
import {viewSongs} from "../../slices/song/thunk";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { useTranslation } from 'react-i18next';
import {normalizeSongText, songQueryTokens, matchesSongQuery, songMatchRank} from "../../helpers/songSearch";

const SearchOption = ({addForm, songOptions, event = null, isEventAdmin = false, loadFullDatabase = false}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [isManualClose, setIsManualClose] = useState(false);
  const [showBottomArrow, setShowBottomArrow] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const dispatch = useDispatch();

  // Check if guest can add songs from outside database
  const canAddFromOutside = event?.allowGuestsAddSongsFromDatabaseOutside !== false || isEventAdmin;

  // Only fetch songs if songOptions not provided
  useEffect(() => {
    if (!songOptions) {
      const eventId = event?.id || null;
      dispatch(viewSongs(eventId));
    }
  }, [dispatch, songOptions, event]);

  const selectSongList = createSelector(
    (state) => state.SongList,
    (slice) => ({
      songList: slice.list,
    })
  );
  const {songList} = useSelector(selectSongList);

  // Use provided songOptions or fetch and format from songList
  const optionsSong = useMemo(() => {
    if (songOptions) {
      return songOptions;
    }
    return songList.map((item) => ({
      label: item.song,
      value: item.id,
    }));
  }, [songOptions, songList]);

  useEffect(() => {
    if (isManualClose) return;

    if (value.length > 0) {
      const normalizedValue = normalizeSongText(value);
      const tokens = songQueryTokens(value);

      const filtered = optionsSong
        .filter(option => matchesSongQuery(normalizeSongText(option.label), tokens))
        .sort((a, b) => songMatchRank(normalizeSongText(a.label), normalizedValue)
          - songMatchRank(normalizeSongText(b.label), normalizedValue));

      setFilteredOptions(filtered);
      // Show dropdown even if no results when guest can't add from outside
      setIsOpen(filtered.length > 0 || !canAddFromOutside);
    } else if (loadFullDatabase && isOpen) {
      // If loadFullDatabase is true and dropdown is open (user focused), show all options
      setFilteredOptions(optionsSong);
    } else {
      setIsOpen(false);
      setFilteredOptions([]);
    }
  }, [value, isManualClose, optionsSong, canAddFromOutside, loadFullDatabase, isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setIsOpen(false);
        setIsManualClose(true);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sprawdź czy jest więcej treści do przewinięcia
  const checkScroll = () => {
    if (!scrollContainerRef.current) return;

    // SimpleBar zwraca obiekt z metodą getScrollElement()
    const scrollableElement = scrollContainerRef.current.getScrollElement
      ? scrollContainerRef.current.getScrollElement()
      : scrollContainerRef.current;

    if (!scrollableElement) return;

    const hasMoreContent = scrollableElement.scrollHeight > scrollableElement.clientHeight;
    const isAtBottom = scrollableElement.scrollTop + scrollableElement.clientHeight >= scrollableElement.scrollHeight - 5;

    setShowBottomArrow(hasMoreContent && !isAtBottom);
  };

  useEffect(() => {
    if (!scrollContainerRef.current || !isOpen) return;

    // SimpleBar zwraca obiekt z metodą getScrollElement()
    const scrollableElement = scrollContainerRef.current.getScrollElement
      ? scrollContainerRef.current.getScrollElement()
      : scrollContainerRef.current;

    if (!scrollableElement) return;

    // Sprawdź przy otwarciu dropdown i zmianie opcji
    setTimeout(checkScroll, 100);

    // Dodaj listener na scroll
    scrollableElement.addEventListener('scroll', checkScroll);

    return () => {
      scrollableElement.removeEventListener('scroll', checkScroll);
    };
  }, [isOpen, filteredOptions]);

  const handleInputChange = (e) => {
    setIsManualClose(false);
    const newValue = e.target.value;
    setValue(newValue);

    // If guest can't add from outside, don't set song value unless it matches an option
    if (!canAddFromOutside) {
      // Sam wpisany tekst nie jest utworem — wartość ustawia dopiero wybór z listy.
      addForm.setFieldValue('song', null);
    } else {
      // Normal behavior - set the text value
      addForm.setFieldValue('song', newValue);
    }
  };

  const handleOptionClick = (option) => {
    setIsManualClose(true);
    setValue(option.label);
    setIsOpen(false);
    if (addForm.touched.song && addForm.errors.song) addForm.resetForm();
    addForm.setFieldValue('song', option.value);
  };

  const handleClearClick = () => {
    setIsManualClose(false);
    setValue("");
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    setIsManualClose(false);
    // If loadFullDatabase is true, show all options on focus even if value is empty
    if (loadFullDatabase && value.length === 0) {
      setFilteredOptions(optionsSong);
      setIsOpen(true);
    } else if (value.length > 0) {
      // Otwórz dropdown tylko jeśli jest co najmniej 1 litera
      setIsOpen(true);
    }
  };

  return (
    <React.Fragment>
      <div className="app-search d-md-block ps-0 pb-0 pt-0">
        <div>
          {/*<label htmlFor="song" className="form-label">{t('Search For a Song')}</label>*/}
          {/*<Alert className="alert-label-icon label-arrow">*/}
          {/*  <i className="ri-information-line label-icon"></i>*/}
          {/*  {canAddFromOutside ? (*/}
          {/*    <>{t("Search for a song or enter a non-existent one")} {t("in a format")}: <strong>"{t("Artist")} - {t("Title")}"</strong>.</>*/}
          {/*  ) : (*/}
          {/*    <>{t("Search for a song from the list")}</>*/}
          {/*  )}*/}
          {/*</Alert>*/}

          <div className="position-relative" ref={inputRef}>
            <Input
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              type="text"
              className={`form-control form-control-lg ${addForm.touched.song && addForm.errors.song ? 'is-invalid' : ''}`}
              placeholder={t("Search...")}
              id="search-song"
              value={value}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              style={{ backgroundImage: 'none' }}
            />
            {addForm.touched.song && addForm.errors.song && !isOpen && (
              <div className="invalid-feedback">{addForm.errors.song}</div>
            )}
            <span className="mdi mdi-magnify search-widget-icon"></span>
            <span
              className={`mdi mdi-close-circle search-widget-icon search-widget-icon-close ${
                value.length === 0 ? "d-none" : ""
              }`}
              id="search-close-options"
              onClick={handleClearClick}
              style={{ cursor: "pointer" }}
            ></span>

            <div
              className={`dropdown-menu ${isOpen ? "show" : ""}`}
              id="search-dropdown"
              ref={dropdownRef}
              style={{ width: "100%", zIndex: 1050 }}
            >
              <SimpleBar
                ref={scrollContainerRef}
                style={{
                  maxHeight: "150px",
                  height: "auto"
                }}
              >
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option) => (
                    <div
                      style={{ cursor: "pointer" }}
                      key={option.value}
                      className="dropdown-item notify-item fs-15 ps-2"
                      onClick={(e) => {
                        e.preventDefault();
                        handleOptionClick(option);
                      }}
                    >
                      <span>{option.label}</span>
                    </div>
                  ))
                ) : (
                  !canAddFromOutside && (
                    <div className="dropdown-item notify-item fs-15 ps-2 text-muted">
                      <span>{t('Not found')}</span>
                    </div>
                  )
                )}
              </SimpleBar>

              {/* Wskaźnik przewijania w dół */}
              {showBottomArrow && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "-7px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    pointerEvents: "none",
                    zIndex: 10
                  }}
                >
                  <i className="bx bx-chevrons-down fs-2" style={{opacity: 0.5}}></i>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default SearchOption;
